import type { BackgammonSocket } from '@qlashgeminiz/qgammon-board-ui';

import type { QcpMatchSocket } from '@/hooks/useQcpMatch';
import type { ActiveMatchDescriptor } from '@/store/activeMatch.slice';

/** Game-agnostic snapshot the pill reads (no game-specific fields leak out). */
export interface ActiveMatchProjection {
  phase: 'yourTurn' | 'waiting' | 'ended';
  /** Optional clock of the player currently on the move, in whole seconds. */
  clock?: { seconds: number; owner: 'me' | 'opponent' };
}

/**
 * QGammon → projection. `ended` (whole-match end) wins; otherwise the phase is
 * "yourTurn" when the server turn matches our perspective and we're moving,
 * else "waiting". Clock omitted in v1 (state.clock shape varies; add later).
 */
export function projectQgammon(
  sock: BackgammonSocket,
  descriptor: ActiveMatchDescriptor,
): ActiveMatchProjection {
  if (sock.ended) return { phase: 'ended' };
  const game = sock.state?.game ?? null;
  const mine = !!game && game.turn === descriptor.perspective && game.phase === 'moving';
  return { phase: mine ? 'yourTurn' : 'waiting' };
}

/**
 * QCP → projection. `matchResult` (set end) wins; otherwise "yourTurn" when the
 * server-authoritative acting seat (`state.public.turn`) is ours. QCP placement
 * is strictly alternating — the gameserver's `GameSession.apply()` flips
 * `public.turn` to the other seat on every accepted placement and rebroadcasts
 * `match:state` — so `turn` is reliable where `draw` is not: `draw` is only set
 * on the Deal event and cleared on HandStart, NOT on `place()`, so it stays
 * populated while we're actually waiting on the opponent. Gated on `draw.length
 * > 0` too so a state/deal race (turn flips to us before our fresh Deal arrives)
 * conservatively reports "waiting" rather than a false "yourTurn".
 */
export function projectQcp(sock: QcpMatchSocket): ActiveMatchProjection {
  if (sock.matchResult) return { phase: 'ended' };
  const myTurn =
    sock.mySeat != null &&
    sock.state?.public.turn === sock.mySeat &&
    sock.draw.length > 0;
  return { phase: myTurn ? 'yourTurn' : 'waiting' };
}
